/**
 * @file vuexstore to handle draft posts
 * Draft posts are show in the post modal but not saved to the server yet
 * This module is dependent on the post module to get cid of the post being edited
 */

import { __ } from 'bits/intl'
import sortBy from 'lodash.sortby'
import buildPost from 'surface/build_post'
import { Cid, DraftPost, Post, PostAttributes } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'
import { isEmpty, isNew } from 'vuexstore/helpers/post'
import { isNew as isSectionNew, Section } from 'vuexstore/helpers/section'
import { RootState } from 'vuexstore/surface/types'

interface DraftState {
  draftByCid: Record<Cid, DraftPost>
  // when a minimized draft has no subject, we will use "Draft 1", "Draft 2", ... as its title,
  // this counter is to keep track of the number to be used
  minimizedDraftPlaceholderTitleCount: number
  draftShowingMinimizedContextMenuCid: Cid | null
  // when a single file/text/link is dropped/pasted on the padlet page, we will open a new docked draft
  // if we are in shelf format, the draft will go to "pending mode" and we have to open the section picker to select a column first
  // => store the new draft's cid here so we can open it later once a column is selected
  latestPendingDraftCid: Cid | null
}

const initialState = (): DraftState => {
  return {
    draftByCid: {},
    minimizedDraftPlaceholderTitleCount: 0,
    draftShowingMinimizedContextMenuCid: null,
    latestPendingDraftCid: null,
  }
}

const draft: Module<DraftState, RootState> = {
  namespaced: true,
  state: initialState,
  getters: {
    draftByCid: (state): Function => (cid: Cid | null | undefined): DraftPost | undefined =>
      cid ? state.draftByCid[cid] : undefined,
    draftCids: (state, getters): Cid[] =>
      sortBy(Object.keys(state.draftByCid), (postCid) => -1 * getters.draftByCid(postCid).draftCreatedAt),
    // Draft posts that are not being edited, i.e. minimized
    standByDraftCids: (state, getters, _, rootGetters): Cid[] =>
      getters.draftCids.filter((postCid) => postCid !== rootGetters['post/postBeingEditedCid']),
    // The draft post being edited
    activeDraft: (state, getters, _, rootGetters): DraftPost | null => {
      return rootGetters['post/postBeingEditedCid'] ? getters.draftByCid(rootGetters['post/postBeingEditedCid']) : null
    },
    activeDraftCid: (state, getters): Cid | null => getters.activeDraft?.cid,
    isAnyDraftActive: (state, getters): boolean => !!getters.activeDraftCid,
    isCidActiveDraft: (state, getters): Function => (cid: Cid): boolean => !!cid && getters.activeDraftCid === cid,
    isCidBeingDrafted: (state, getters): Function => (cid: Cid): boolean => !!getters.draftByCid(cid),
    isDraftCidNewPost: (state, getters): Function => (cid: Cid): boolean =>
      !!getters.draftByCid(cid) && isNew(getters.draftByCid(cid)),
    isActiveDraftNewPost: (state, getters): boolean => getters.isDraftCidNewPost(getters.activeDraftCid),
    activeDraftUploadingFile: (state, getters): File | undefined | null => getters.activeDraft?.uploadingFile,
    draftShowingMinimizedContextMenuCid: (state): Cid | null => state.draftShowingMinimizedContextMenuCid,
  },
  mutations: {
    UPDATE_DRAFT(state, post: DraftPost): void {
      Vue.set(state.draftByCid, post.cid, post)
    },
    REMOVE_DRAFT(state, postCid: Cid): void {
      Vue.delete(state.draftByCid, postCid)
    },
    SAVE_DRAFT_PLACEHOLDER_TITLE(state, postCid: Cid): void {
      state.minimizedDraftPlaceholderTitleCount += 1
      const post = state.draftByCid[postCid]
      Vue.set(state.draftByCid, post.cid, {
        ...post,
        minimizedPlaceholderTitle: __('Draft %{draftNumber}', {
          draftNumber: state.minimizedDraftPlaceholderTitleCount,
        }),
      })
    },
    REMOVE_DRAFT_ATTACHMENT(state, { postCid }: { postCid: Cid }): void {
      Vue.set(state.draftByCid[postCid], 'attachment', '')
      Vue.set(state.draftByCid[postCid], 'attachment_caption', null)
    },
    SHOW_MINIMIZED_CONTEXT_MENU(state, cid): void {
      state.draftShowingMinimizedContextMenuCid = cid
    },
    HIDE_MINIMIZED_CONTEXT_MENU(state): void {
      state.draftShowingMinimizedContextMenuCid = null
    },
    SET_LATEST_PENDING_DRAFT_CID(state, cid): void {
      state.latestPendingDraftCid = cid
    },
    AUTO_PUBLISH_DRAFT(state, cid: Cid): void {
      Vue.set(state.draftByCid[cid], 'shouldAutoPublish', true)
    },
    STOP_AUTO_PUBLISH_DRAFT(state, cid: Cid): void {
      Vue.set(state.draftByCid[cid], 'shouldAutoPublish', false)
    },
  },
  actions: {
    startNewDraft(
      { dispatch, rootGetters, rootState, commit },
      {
        attributes,
        shouldStartEditing,
      }: { attributes: Post | DraftPost | PostAttributes; shouldStartEditing: boolean },
    ): void {
      const post = buildPost(attributes, {
        state: rootState,
        getters: rootGetters,
      })
      const usableSections = rootGetters['section/sections'].filter((x: Section): boolean => !isSectionNew(x))
      if (rootGetters.isShelf && !post.wall_section_id && usableSections.length !== 1) {
        dispatch('post/createPendingPost', { postAttributes: { ...post, file: attributes.file } }, { root: true })
        if (shouldStartEditing) {
          commit('SET_LATEST_PENDING_DRAFT_CID', post.cid)
        }
        return
      }

      if (rootGetters.isShelf && !post.wall_section_id && usableSections.length === 1) {
        post.wall_section_id = usableSections[0].id
      }
      dispatch('post/updatePostInStore', { ...post, subject: '', body: '', attachment: '' }, { root: true })
      dispatch('updateDraft', post)
      dispatch('setDraftNumber', { cid: post.cid })
      if (shouldStartEditing) {
        dispatch('post/startEditingPost', { postCid: post.cid }, { root: true })
      }
      if (attributes.file) {
        dispatch('startUploadFileInDraft', { file: attributes.file, cid: post.cid })
      }
    },
    updateDraft({ commit, getters }, post: Post | DraftPost | PostAttributes): void {
      if (!post.cid) {
        return
      }
      const existingDraft = getters.draftByCid(post.cid)
      const newDraft = { ...existingDraft, ...post }
      if (!newDraft.draftCreatedAt) {
        newDraft.draftCreatedAt = Date.now()
      }

      // reset attachment caption when attachment changed
      if (
        !!existingDraft &&
        post.attachment &&
        existingDraft.attachment &&
        post.attachment !== existingDraft.attachment
      ) {
        newDraft.attachment_caption = null
      }
      commit('UPDATE_DRAFT', newDraft)
    },
    updateActiveDraft({ dispatch, getters }, post: Post | DraftPost | PostAttributes): void {
      dispatch('updateDraft', { ...getters.activeDraft, ...post })
    },
    removeDraft({ commit, dispatch, getters }, postCid: Cid): void {
      if (postCid === getters.activeDraftCid) {
        dispatch('stopUploadFileActiveDraft')
        dispatch('post/stopEditingPost', null, { root: true })
      }
      commit('REMOVE_DRAFT', postCid)
    },
    setDraftNumber({ commit, getters }, { cid }: { cid: Cid | null | undefined }): void {
      // set the title for minimized draft to "Draft 1", "Draft 2", ... if necessary
      const draftPost = cid ? getters.draftByCid(cid) : null
      if (draftPost && !draftPost.subject && !draftPost.minimizedPlaceholderTitle) {
        commit('SAVE_DRAFT_PLACEHOLDER_TITLE', cid)
      }
    },
    createNewDraftFromPendingUpload(
      { dispatch, rootState, rootGetters, state, commit },
      { file, postAttributes }: { file: File; postAttributes: Post },
    ): void {
      // Create the post inside post module
      const newPostAttributes = buildPost(postAttributes, {
        state: rootState,
        getters: rootGetters,
      })

      dispatch('post/updatePostInStore', { ...newPostAttributes, subject: '' }, { root: true })

      // Create the draft
      dispatch('updateDraft', { ...newPostAttributes, subject: postAttributes.subject || '' })
      dispatch('setDraftNumber', { cid: newPostAttributes.cid })

      // Start upload
      dispatch('startUploadFileInDraft', { file, cid: newPostAttributes.cid })

      if (newPostAttributes.cid === state.latestPendingDraftCid) {
        dispatch('post/startEditingPost', { postCid: newPostAttributes.cid }, { root: true })
        commit('SET_LATEST_PENDING_DRAFT_CID', null)
      }
    },
    cancelEditingDraft({ dispatch, getters }): void {
      if (getters.activeDraftCid) {
        if (isNew(getters.activeDraft)) {
          dispatch('post/deletePost', { postCid: getters.activeDraftCid }, { root: true })
        }
        dispatch('removeDraft', getters.activeDraftCid)
      }
    },
    removeDraftAttachment({ commit, getters }, { postCid }: { postCid: Cid }): void {
      if (getters.draftByCid(postCid)) {
        commit('REMOVE_DRAFT_ATTACHMENT', { postCid })
      }
    },
    // if there is not custom maxFileUploadSize, the default limit based on user membership is used
    startUploadFileInDraft(
      { dispatch },
      { file, maxFileUploadSize, cid }: { file: File; maxFileUploadSize?: number; cid: Cid },
    ): void {
      dispatch('updateDraft', { cid, maxFileUploadSize, uploadingFile: file, uploadProgress: 0 })
    },
    stopUploadFileInDraft({ dispatch }, { cid }: { cid: Cid }): void {
      dispatch('updateDraft', { cid, uploadingFile: null })
    },
    uploadFileActiveDraft(
      { dispatch, getters },
      { file, maxFileUploadSize }: { file: File; maxFileUploadSize?: number },
    ): void {
      dispatch('startUploadFileInDraft', { file, maxFileUploadSize, cid: getters.activeDraftCid })
    },
    stopUploadFileActiveDraft({ dispatch, getters }): void {
      dispatch('stopUploadFileInDraft', { cid: getters.activeDraftCid })
    },
    async replaceActiveDraftUploadingFile({ dispatch, getters }, { file }: { file: File }): Promise<void> {
      if (getters.activeDraft?.attachment) {
        // The previous attachment upload/adding has finished, remove the existing attachment
        dispatch('removeDraftAttachment', { postCid: getters.activeDraft.cid })
      } else if (getters.activeDraftUploadingFile) {
        // Another file is being uploaded, stop that upload first
        // need await to ensure that stop upload and start new upload happens sequentially
        await dispatch('stopUploadFileActiveDraft')
      }

      dispatch('uploadFileActiveDraft', { file })
    },
    async replaceActiveDraftAttachmentWithLink({ dispatch, getters }, { link }: { link: string }): Promise<void> {
      // replace the active draft's attachment/uploading file when a link is dropped on to it
      if (getters.activeDraft?.attachment) {
        // An attachment already exists or a file upload is done => remove it
        // await to make sure the attachment removal and adding the new attachment happens sequentially
        await dispatch('removeDraftAttachment', { postCid: getters.activeDraft.cid })
      } else if (getters.activeDraftUploadingFile) {
        // A file is being uploaded, stop that upload first
        // await to ensure that stop upload and adding the new attachment happens sequentially
        await dispatch('stopUploadFileActiveDraft')
      }

      dispatch('updateActiveDraft', { attachment: link })
    },
    openMinimizedContextMenu({ commit }, { cid }): void {
      commit('SHOW_MINIMIZED_CONTEXT_MENU', cid)
    },
    closeMinimizedContextMenu({ commit }): void {
      commit('HIDE_MINIMIZED_CONTEXT_MENU')
    },
    autoPublishDraft({ commit }, { cid }): void {
      commit('AUTO_PUBLISH_DRAFT', cid)
    },
    stopAutoPublishDraft({ commit }, { cid }): void {
      commit('STOP_AUTO_PUBLISH_DRAFT', cid)
    },
    publishDraft({ dispatch, getters }, { cid }): void {
      const existingDraft = getters.draftByCid(cid)

      if (!existingDraft) {
        return
      }

      // If a file is being uploaded in the draft, set it to auto publish
      if (existingDraft.uploadingFile) {
        dispatch('autoPublishDraft', { cid })
      } else if (!isEmpty(existingDraft as DraftPost)) {
        dispatch('post/savePost', { attributes: existingDraft }, { root: true })
      } else {
        dispatch('post/deletePost', { postCid: cid }, { root: true })
      }
    },
    deleteAllDrafts({ dispatch, getters }): void {
      getters.draftCids.forEach((cid) => {
        dispatch('post/deletePost', { postCid: cid }, { root: true })
      })
    },
  },
}

export default draft
export { DraftState }
